import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Copy, Link2, Unlink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, statusOf } from "@/lib/api";
import { useHub } from "@/components/AppShell";
import { PlanLock } from "@/components/billing/Plans";
import { Loader, NoAccess, PageHeader } from "@/components/common";
import { Confirm } from "@/components/kids/kidsUi";
import { useLiveRefresh } from "@/lib/live";
import { Custody } from "@/components/coparent/Custody";
import { Shared } from "@/components/coparent/Shared";
import { Notes } from "@/components/coparent/Notes";
import { Money } from "@/components/coparent/Money";

const RGB = "var(--glow-family)";
const TABS = [["custody", "Custody"], ["info", "Shared info"], ["notes", "Handoff notes"], ["money", "Reimbursements"]];

function Invite({ hubBase, k, can, reload }) {
  const { hub } = useHub();
  const make = () => api.post(`${hubBase}/invites`, { kid_id: k.id }).then(reload).catch((e) => toast.error(errMsg(e)));
  const url = k.invite && `${window.location.origin}/coparent/join/${k.invite.token}`;
  if (!k.invite && hub?.tier?.features?.links_max === 0) return <PlanLock hub={hub} what="Start a co-parenting link with another household." testid={`cp-plan-lock-${k.id}`} />;
  if (!k.invite) return <button className="q-btn q-btn-primary min-h-[44px]" disabled={!can} onClick={make} data-testid={`cp-invite-${k.id}`}><Link2 className="h-4 w-4" /> Link with my co-parent</button>;
  return (
    <div className="w-full space-y-2 rounded-lg border border-q-line p-3 text-sm" data-testid={`cp-invite-box-${k.id}`}>
      <div className="text-q-muted">Send your co-parent this link and code. It expires {new Date(k.invite.expires_at).toLocaleDateString()}.</div>
      <div className="flex min-w-0 items-center gap-2"><code className="min-w-0 flex-1 truncate rounded bg-q-raised px-2 py-2 text-xs" data-testid={`cp-invite-url-${k.id}`}>{url}</code>
        <button className="q-btn q-btn-ghost h-11 w-11 shrink-0 p-0" onClick={() => navigator.clipboard?.writeText(url).then(() => toast.success("Link copied"))} aria-label="Copy link" data-testid={`cp-invite-copy-${k.id}`}><Copy className="h-4 w-4" /></button></div>
      <div className="font-display text-2xl font-extrabold tracking-[0.3em] text-q-text" data-testid={`cp-invite-code-${k.id}`}>{k.invite.code}</div>
    </div>
  );
}

function CopyView({ hubBase, id, onClose }) {
  const [c, setC] = useState(null);
  useEffect(() => { api.get(`${hubBase}/copies/${id}`).then(({ data }) => setC(data)).catch((e) => toast.error(errMsg(e))); }, [hubBase, id]);
  if (!c) return <Loader />;
  return (
    <section className="q-card space-y-3 p-4" data-testid="cp-copy-view">
      <div className="flex items-center justify-between gap-2"><h3 className="font-display text-lg font-extrabold text-q-text">{c.kid_name} · copy from when linked with {c.other_hub_name}</h3>
        <button className="q-btn q-btn-ghost min-h-[44px]" onClick={onClose} data-testid="cp-copy-close">Close</button></div>
      <div className="text-xs text-q-faint">Unlinked {new Date(c.unlinked_at).toLocaleString()} by {c.by_name}. Nothing is shared any more.</div>
      <div className="grid gap-2 text-sm sm:grid-cols-2">{Object.entries(c.info).map(([k, v]) => <div key={k} className="break-words"><b className="text-q-text">{k}</b>: <span className="text-q-muted">{Array.isArray(v) ? v.map((x) => (typeof x === "string" ? x : x.allergen || x.name)).join(", ") || "—" : v || "—"}</span></div>)}</div>
      <div className="text-sm text-q-muted">{c.log.length} log entries · {c.notes.length} handoff notes · {c.reimbursements.length} reimbursements kept</div>
    </section>
  );
}

function LinkView({ hubBase, linkId }) {
  const [sp, setSp] = useSearchParams();
  const [l, setL] = useState(null);
  const [unlink, setUnlink] = useState(false);
  const base = `${hubBase}/links/${linkId}`;
  const load = useCallback(() => api.get(base).then(({ data }) => setL(data)).catch((e) => toast.error(errMsg(e))), [base]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load, (m) => m.entity === "coparent", 20000);
  if (!l) return <Loader />;
  const tab = sp.get("tab") || "custody";
  const doUnlink = () => api.post(`${base}/unlink`, { confirm: true }).then(() => { toast.success(`${l.kid.name} is unlinked. You keep a copy.`); setSp({}); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="min-w-0 space-y-5" data-testid="cp-link-view">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto min-w-0"><div className="font-display text-xl font-extrabold text-q-text" data-testid="cp-link-title">{l.kid.name}</div>
          <div className="text-sm text-q-muted">Linked between <span style={{ color: l.me.color }}>{l.me.name}</span> and <span style={{ color: l.other.color }}>{l.other.name}</span></div></div>
        {l.is_parent && <button className="q-btn q-btn-ghost min-h-[44px] text-red-300" disabled={!l.can_write} onClick={() => setUnlink(true)} data-testid="cp-unlink-btn"><Unlink className="h-4 w-4" /> Unlink</button>}
      </div>
      {l.lapsed.me && <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200" data-testid="cp-lapsed-banner">This hub is read-only. You can view the custody calendar and shared info. Renew to make changes.</div>}
      {l.lapsed.other && <div className="rounded-lg border border-q-line p-3 text-sm text-q-muted" data-testid="cp-other-lapsed">{l.other.name} is read-only right now. You can still answer their requests here.</div>}
      <div className="flex max-w-full gap-1 overflow-x-auto rounded-full border border-q-line p-1" role="tablist">{TABS.map(([k, t]) => (
        <button key={k} role="tab" aria-selected={tab === k} className={`min-h-[44px] shrink-0 whitespace-nowrap rounded-full px-4 text-sm font-semibold ${tab === k ? "bg-q-raised text-q-text" : "text-q-muted"}`} onClick={() => setSp({ link: linkId, tab: k })} data-testid={`cp-tab-${k}`}>{t}</button>))}</div>
      {tab === "custody" && <Custody base={base} l={l} reload={load} />}
      {tab === "info" && <Shared base={base} l={l} reload={load} />}
      {tab === "notes" && <Notes base={base} hubBase={hubBase} l={l} reload={load} />}
      {tab === "money" && <Money base={base} hubBase={hubBase} l={l} reload={load} />}
      <Confirm open={unlink} onClose={() => setUnlink(false)} title={`Unlink ${l.kid.name}?`} confirmLabel="Unlink" onConfirm={doUnlink} testid="cp-unlink-confirm"
        body={`Each home keeps its own copy of the shared info and history, and nothing is shared from then on. ${l.other.name} will be told.`} />
    </div>
  );
}

export default function Coparent() {
  const { hub } = useHub();
  const hubBase = `/organizations/${hub.id}/coparent`;
  const [sp, setSp] = useSearchParams();
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const [copyId, setCopyId] = useState(null);
  const load = useCallback(() => api.get(hubBase).then(({ data }) => setD(data)).catch((e) => (statusOf(e) === 403 ? setDenied(true) : toast.error(errMsg(e)))), [hubBase]);
  useEffect(() => { load(); }, [load, sp]);
  useLiveRefresh(load, (m) => m.entity === "coparent", 30000);
  if (denied) return <NoAccess />;
  if (!d) return <Loader />;
  const linked = d.kids.filter((k) => k.link_id);
  const active = sp.get("link") && linked.some((k) => k.link_id === sp.get("link")) ? sp.get("link") : linked[0]?.link_id;
  return (
    <div className="min-w-0" data-testid="coparent-page">
      <PageHeader eyebrow="Family" title="Co-parenting" rgb={RGB} testid="coparent-title" />
      <div className="mb-6 flex flex-wrap gap-2" data-testid="cp-kids">
        {d.kids.map((k) => (
          <div key={k.id} className={`q-card flex min-w-0 flex-wrap items-center gap-3 p-3 ${k.link_id === active ? "ring-1 ring-q-faint" : ""}`} data-testid={`cp-kid-${k.id}`}>
            {k.link_id ? <button className="min-h-[44px] text-left" onClick={() => setSp({ link: k.link_id })} data-testid={`cp-kid-open-${k.id}`}><b className="text-q-text">{k.name}</b>
              <span className="block text-xs text-q-muted">Linked with {k.other_hub}</span></button>
              : <><b className="text-q-text">{k.name}</b>{d.is_parent && <Invite hubBase={hubBase} k={k} can={d.can_write} reload={load} />}</>}
            {k.copies.map((c) => <button key={c.id} className="q-btn q-btn-ghost min-h-[44px] text-xs" onClick={() => setCopyId(c.id)} data-testid={`cp-copy-${c.id}`}>Copy from {c.other_hub_name}</button>)}
          </div>))}
      </div>
      {copyId && <div className="mb-6"><CopyView hubBase={hubBase} id={copyId} onClose={() => setCopyId(null)} /></div>}
      {active ? <LinkView key={active} hubBase={hubBase} linkId={active} /> : <p className="text-sm text-q-muted" data-testid="cp-none">No linked kids yet. A parent can tap “Link with my co-parent” on a kid.</p>}
    </div>
  );
}
